import React, { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import { FaCheckCircle } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { faEye, faEyeSlash, faHome } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import {
  SendEmailOtp,
  VerifyEmailOtp,
  SendPhoneOtp,
  VerifyPhoneOtp,
  SubmitRegistration,
} from "../../../../features/admin/auth/Slice";
import {
  AllStates,
  AllCity,
  GetVendorFullDetails,
} from "../../../../features/admin/common/Slice";

function Personal_Details({ nextStep, setUserId, userId }) {
  const dispatch = useDispatch();

  // OTP / verification related backend error messages
  const [emailOtpError, setEmailOtpError] = useState("");
  const [mobile_numberOtpError, setMobile_numberOtpError] = useState("");

  // Password show/hide
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Keep icon toggle based on boolean states
  const iconFor = (visible) => (visible ? faEye : faEyeSlash);

  // Redux selectors
  const vendorData = useSelector((state) => state.common.all_vendor_details);
  const { all_states, all_city } = useSelector((state) => state.common);

  // React Hook Form
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm();

  const selectedState = watch("state");

  // OTP states
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [emailOtp, setEmailOtp] = useState(["", "", "", ""]);
  const [phoneOtp, setPhoneOtp] = useState(["", "", "", ""]);

  const [showEmailOtp, setShowEmailOtp] = useState(false);
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);

  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const emailOtpRefs = useRef([]);
  const phoneOtpRefs = useRef([]);

  // Timers
  const [emailTimer, setEmailTimer] = useState(0);
  const [isEmailRunning, setIsEmailRunning] = useState(false);
  const [phoneTimer, setPhoneTimer] = useState(0);
  const [isPhoneRunning, setIsPhoneRunning] = useState(false);

  // Backend validation error containers for email/mobile inputs
  const [emailError, setEmailError] = useState("");
  const [mobileNumberError, setMobileNumberError] = useState("");

  // Fetch vendor details when userId exists
  useEffect(() => {
    if (userId) {
      dispatch(GetVendorFullDetails(userId));
    }
  }, [userId, dispatch]);

  // If editing existing user, mark phone & email verified (keeps previous behavior)
  useEffect(() => {
    if (userId) {
      setPhoneVerified(true);
      setEmailVerified(true);
    }
  }, [userId]);

  const email = watch("email");
  const mobile_number = watch("mobile_number");

  // When the email field changes, we must un-verify email (existing behavior)
  useEffect(() => {
    if (emailVerified) {
      setEmailVerified(false);
    }
  }, [email]);

  // When mobile number field changes, un-verify phone (existing behavior)
  useEffect(() => {
    if (phoneVerified) {
      setPhoneVerified(false);
    }
  }, [mobile_number]);

  // Prefill form when vendor data arrives
  useEffect(() => {
    if (vendorData?.user_details) {
      const vd = vendorData.user_details;
      const vEM = vendorData.vendor;

      reset({
        email: vEM.email || "",
        mobile_number: vEM.mobile_number || "",
        alternate_mobile_no: vd.alternate_mobile_no || "",
        address: vd.address || "",
        pincode: vd.pincode || "",
        state: vd.state_id || "",
        city: vd.city_id || "",
      });
    }
  }, [vendorData, reset]);

  // Email timer
  useEffect(() => {
    let interval;
    if (isEmailRunning && emailTimer > 0) {
      interval = setInterval(() => setEmailTimer((prev) => prev - 1), 1000);
    }
    if (emailTimer === 0) setIsEmailRunning(false);
    return () => clearInterval(interval);
  }, [isEmailRunning, emailTimer]);

  // Phone timer
  useEffect(() => {
    let interval;
    if (isPhoneRunning && phoneTimer > 0) {
      interval = setInterval(() => setPhoneTimer((prev) => prev - 1), 1000);
    }
    if (phoneTimer === 0) setIsPhoneRunning(false);
    return () => clearInterval(interval);
  }, [isPhoneRunning, phoneTimer]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  // Load states once
  useEffect(() => {
    dispatch(AllStates());
  }, [dispatch]);

  // Load cities when a state is selected
  useEffect(() => {
    if (selectedState) {
      dispatch(AllCity({ stateId: selectedState }));
    }
  }, [selectedState, dispatch]);

  // OTP input helpers
  const handleOtpChange = (otpArray, setOtp, refs, index, value) => {
    // clear otp-specific backend errors when user types
    setEmailOtpError("");
    setMobile_numberOtpError("");

    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otpArray];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < refs.current.length - 1) {
        refs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (otpArray, refs, index, e) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      refs.current[index - 1].focus();
    }
  };

  // SEND EMAIL OTP
  const sendEmailOtp = async () => {
    const emailVal = watch("email");

    // front-end validation
    if (!emailVal || errors?.email) {
      setEmailError(errors?.email?.message || "Please enter a valid email");
      return;
    }

    try {
      setEmailError("");
      setEmailOtpError("");

      const res = await dispatch(SendEmailOtp({ email: emailVal, user_id: userId })).unwrap();

      if (res?.success) {
        setShowEmailOtp(true);
        // start timer if backend provides or set default (example 120s)
        setEmailTimer(res?.expires_in || 60);
        setIsEmailRunning(true);
      }
    } catch (err) {
      setEmailError(err?.message || "Failed to send OTP");
    }
  };

  // VERIFY EMAIL OTP
  const verifyEmail = async () => {
    const otp = emailOtp.join("");
    const emailVal = watch("email");

    try {
      const res = await dispatch(VerifyEmailOtp({ email: emailVal, otp, user_id: userId })).unwrap();

      if (res?.success) {
        setEmailVerified(true);
        localStorage.setItem("emailVerified", "true");
        setEmailOtpError("");
        setShowEmailOtp(false);
      }
    } catch (err) {
      setEmailOtpError(err?.message || "Invalid Otp");
    }
  };

  // SEND PHONE OTP
  const sendPhoneOtp = async () => {
    const mobileVal = watch("mobile_number");

    if (!mobileVal || errors?.mobile_number) {
      setMobileNumberError(errors?.mobile_number?.message || "Invalid mobile number");
      return;
    }

    try {
      setMobileNumberError("");
      setMobile_numberOtpError("");

      const res = await dispatch(SendPhoneOtp({ mobile_number: mobileVal, user_id: userId })).unwrap();

      if (res?.success) {
        setShowPhoneOtp(true);
        setPhoneTimer(res?.expires_in || 60);
        setIsPhoneRunning(true);
      }
    } catch (err) {
      setMobileNumberError(err?.message || "Invalid Mobile Number");
      setShowPhoneOtp(false);
    }
  };

  // VERIFY PHONE OTP
  const verifyPhone = async () => {
    const otp = phoneOtp.join("");
    const mobileVal = watch("mobile_number");

    try {
      const res = await dispatch(VerifyPhoneOtp({ mobile_number: mobileVal, otp, user_id: userId })).unwrap();

      if (res?.success) {
        setPhoneVerified(true);
        localStorage.setItem("phoneVerified", "true");
        setShowPhoneOtp(false);
        setMobile_numberOtpError("");
      }
    } catch (err) {
      setMobile_numberOtpError(err?.message || "Invalid Otp");
    }
  };

  // FORM SUBMIT
  const handleFormSubmit = async (data) => {
    try {
      // these checks were in your original code - keep them
      if (!emailVerified) return alert("Please verify email");
      // if (!phoneVerified) return alert("Please verify phone");
      if (data.password !== data.confirm_password) {
        alert("Password & Confirm Password must match!");
        return;
      }
      if (!agreeTerms) return alert("Please check the Terms & Conditions box");

      const sendData = new FormData();
      sendData.append("email", data?.email || "");
      sendData.append("mobile_number", data?.mobile_number || "");
      sendData.append("alternate_mobile_no", data?.alternate_mobile_no || "");
      sendData.append("address", data?.address || "");
      sendData.append("pincode", data?.pincode || "");
      sendData.append("state", data?.state || "");
      sendData.append("city", data?.city || "");
      sendData.append("password", data?.password || "");
      if (userId) sendData.append("user_id", userId);

      const res = await dispatch(SubmitRegistration(sendData)).unwrap();
      const newUserId = res?.data?.user_id;
      setUserId(newUserId);
      nextStep();
    }
    catch (error) {
      console.log("Registration Error: ", error);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";

      // Show alert with backend message
      // alert(message);

      // If backend says email already exists
      if (message.toLowerCase().includes("email")) {
        setError("email", { message });
      }

      // If backend says mobile already exists
      if (message.toLowerCase().includes("mobile")) {
        setError("mobile_number", { message });
      }
    }
  };

  return (
    <div className="stepform_form">
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <div className="form-group">
          <div className="row">
            {/* EMAIL */}
            <div className="col-12 mt-3">
              <div className="form-group__element verify_fields_holder">
                <Form.Control
                  type="text"
                  name="email"
                  placeholder="Enter Email Address"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      // message: "Invalid email address",
                    },
                  })}
                  onChange={(e) => {
                    // clear backend email error & react-hook-form error for email on typing
                    setEmailError("");
                    clearErrors("email");
                    setShowEmailOtp(false);
                    setValue("email", e.target.value);
                  }}
                />


                {!emailVerified && !showEmailOtp && (
                  <span className="verify_text" onClick={sendEmailOtp}>
                    Verify Email
                  </span>
                )}

                {emailVerified && (
                  <span className="verify_text">
                    <FaCheckCircle /> Verified
                  </span>
                )}
              </div>

              {errors?.email && <span className="invalid-feedback d-block">{errors.email.message}</span>}
              {emailError && (
                <span className="invalid-feedback d-block mt-1">
                  {emailError}
                </span>
              )}



              {showEmailOtp && !emailVerified && !emailError && (
                <div className="otp-container mt-2">
                  <span
                    className={`resend_otp ${isEmailRunning ? "disabled" : ""}`}
                    onClick={() => !isEmailRunning && sendEmailOtp()}
                  >
                    RESEND OTP  <span> {isEmailRunning && `${formatTime(emailTimer)} Sec`} </span>
                  </span>

                  <div className="otp-boxes">
                    {emailOtp.map((digit, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        value={digit}
                        ref={(el) => (emailOtpRefs.current[index] = el)}
                        onChange={(e) =>
                          handleOtpChange(emailOtp, setEmailOtp, emailOtpRefs, index, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(emailOtp, emailOtpRefs, index, e)}
                        className="otp-input"
                      />
                    ))}
                  </div>

                  <span className="verify_text" onClick={verifyEmail}>
                    Submit
                  </span>


                </div>
              )}

              <div>
                {emailOtpError && (
                  <span className="invalid-feedback d-block mt-2">
                    {emailOtpError}
                  </span>
                )}
              </div>
            </div>

            {/* PHONE */}
            <div className="col-12 mt-3">
              <div className="form-group__element verify_fields_holder">
                <     Form.Control
                  type="text"
                  name="mobile_number"
                  placeholder="Mobile Number"
                  maxLength={10}
                  {...register("mobile_number", {
                    required: "Mobile Number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Mobile number must be exactly 10 digits",
                    },
                  })}
                  onInput={(e) => {
                    // only numeric
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    setMobileNumberError("");
                    clearErrors("mobile_number");
                    setShowPhoneOtp(false);
                    setValue("mobile_number", e.target.value);
                  }}
                />

                {/* {!phoneVerified && !showPhoneOtp && !mobileNumberError && (
                  <span className="verify_text" onClick={sendPhoneOtp}>
                    Verify Mobile Number
                  </span>
                )} */}



                {/* {phoneVerified && (
                  <span className="verify_text">
                    <FaCheckCircle /> Verified
                  </span>
                )} */}

              </div>
              {errors?.mobile_number && (
                <span className="invalid-feedback d-block">{errors.mobile_number.message}</span>
              )}

              {mobileNumberError && (
                <span className="invalid-feedback d-block mt-1">
                  {mobileNumberError}
                </span>
              )}
            </div>
            {showPhoneOtp && !phoneVerified && (
              <div className="col-12 mt-3">

                <>
                  <div className="otp-container mt-2">
                    <span
                      className={`resend_otp ${isPhoneRunning ? "disabled" : ""}`}
                      onClick={() => !isPhoneRunning && sendPhoneOtp()}
                    >
                      RESEND OTP <span> {isPhoneRunning && `${formatTime(phoneTimer)} Sec`} </span>
                    </span>

                    <div className="otp-boxes">
                      {phoneOtp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          value={digit}
                          ref={(el) => (phoneOtpRefs.current[index] = el)}
                          onChange={(e) =>
                            handleOtpChange(phoneOtp, setPhoneOtp, phoneOtpRefs, index, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(phoneOtp, phoneOtpRefs, index, e)}
                          className="otp-input"
                        />
                      ))}
                    </div>
                    <span className="verify_text" onClick={verifyPhone}>
                      Submit
                    </span>


                  </div>

                  <div>
                    {mobile_numberOtpError && (
                      <span className="invalid-feedback d-block mt-2">{mobile_numberOtpError}</span>
                    )}
                  </div>
                </>
              </div>
            )}


            {/* ALTERNATE PHONE */}
            <div className="col-12 mt-3">
              <div className="form-group__element">

                <Form.Control
                  type="text"
                  placeholder="Alternate Phone Number"
                  maxLength={10}
                  {...register("alternate_mobile_no", {

                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Mobile number must be exactly 10 digits",
                    },
                  })}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    clearErrors("alternate_mobile_no");
                    setValue("alternate_mobile_no", e.target.value);
                  }}
                />
              </div>
              {errors?.alternate_mobile_no && (
                <span className="invalid-feedback d-block">{errors.alternate_mobile_no.message}</span>
              )}
            </div>

            {/* ADDRESS */}
            <div className="col-12 mt-3">
              <div className="form-group__element">

                <Form.Control
                  as="textarea"
                  rows={1}
                  placeholder="Full Address"
                  {...register("address", {
                    required: " Address is required",
                  })}
                  onChange={() => clearErrors("address")}
                />
                {errors?.address && <span className="invalid-feedback d-block">{errors.address.message}</span>}
              </div>
            </div>
            {/* PINCODE */}
            <div className="col-12 mt-3">
              <div className="form-group__element">

                <Form.Control
                  type="text"
                  placeholder="Pincode"
                  maxLength={6}
                  className="form-group__input"
                  {...register("pincode", {
                    required: "Pincode is required",
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: "pincode must be exactly 6 digits",
                    },
                  })}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    clearErrors("pincode");
                    setValue("pincode", e.target.value);
                  }}
                />
              </div>
              {errors?.pincode && (
                <span className="invalid-feedback d-block">
                  {errors.pincode.message}
                </span>
              )}
            </div>


            {/* STATE */}
            <div className="col-md-6 mt-3">
              <div className="form-group__element">

                <Controller
                  name="state"
                  control={control}
                  rules={{ required: "State is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={all_states}
                      value={all_states.find((item) => item.value === field.value) || null}
                      onChange={(val) => {
                        field.onChange(val?.value);
                        clearErrors("state");
                      }}
                      placeholder="Select State"
                    />
                  )}
                />

                {errors.state && (
                  <span className="invalid-feedback d-block">{errors.state.message}</span>
                )}
              </div >
            </div>

            {/* CITY */}
            < div className="col-md-6 mt-3" >
              <div className="form-group__element">

                <Controller
                  name="city"
                  control={control}
                  rules={{ required: "City is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={all_city}
                      value={all_city.find((item) => item.value === field.value) || null}
                      onChange={(val) => {
                        field.onChange(val?.value);
                        clearErrors("city");
                      }}
                      placeholder="Select City"
                    />
                  )}
                />


                {errors?.city && (
                  <span className="invalid-feedback d-block">{errors.city.message}</span>
                )}
              </div >
            </div>

            {/* PASSWORD */}
            < div className="col-md-6 mt-3" >
              <div className="form-group__element">

                <div className="password_field_holder">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter Password"
                    {...register("password", {
                      required: "Password is required",
                      validate: (value) => {
                        if (!value) return "Password is required";
                        if (value.length < 8) return "Password must be at least 8 characters long";
                        if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(value))
                          return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
                        return true;
                      },
                    })}
                    onChange={(e) => {
                      clearErrors("password");
                      setValue("password", e.target.value);
                    }}
                  />
                  <div className="eye-icc" >
                    <FontAwesomeIcon icon={iconFor(showPassword)} className="icon" onClick={() => setShowPassword((s) => !s)} />
                  </div>
                </div>
              </div>
              {errors?.password && <span className="invalid-feedback d-block">{errors.password.message}</span>}
            </div >

            {/* CONFIRM PASSWORD */}
            < div className="col-md-6 mt-3 " >
              <div className="form-group__element">

                <div className="password_field_holder">

                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    {...register("confirm_password", {
                      required: !userId ? "Confirm Password is required" : false,
                      validate: (value) => (!userId ? value === watch("password") || "Passwords do not match" : true),
                    })}
                    onChange={(e) => {
                      clearErrors("confirm_password");
                      setValue("confirm_password", e.target.value);
                    }}
                  />
                  <div className="eye-icc" >
                    <FontAwesomeIcon
                      icon={iconFor(showConfirmPassword)}
                      className="icon"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                    />
                  </div>
                </div>
              </div>
              {errors?.confirm_password && <span className="invalid-feedback d-block">{errors.confirm_password.message}</span>}
            </div >
          </div >

          <div className="col-12 mt-3">

            <Form.Group className="mb-3" controlId="formBasicCheckbox">
              <Form.Check
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                label={
                  <>
                    By continuing, I agree to the{" "}
                    <Link to={"/terms-ans-conditions"}>
                      <b>Terms & Conditions</b>
                    </Link>{" "}
                    &{" "}
                    <Link to={"/privacy-policy"}>
                      <b>Privacy Policy</b>
                    </Link>
                  </>
                }
              />
            </Form.Group>
          </div>
        </div >

        {/* BUTTON */}
        < div className="buttons" style={{ textAlign: "center" }
        }>
          <Button type="submit" className="search_btn form_btn mt-4">
            Save and Next
          </Button>
        </div >
      </form >
    </div >
  );
}

export default Personal_Details;


// import React, { useEffect, useRef, useState } from "react";
// import { Button, Form } from "react-bootstrap";
// import Select from "react-select";
// import { FaCheckCircle } from "react-icons/fa";
// import { Stepper } from "react-form-stepper";
// import { Link } from "react-router-dom";

// function Personal_Details({ nextStep, prevStep }) {
//   // OTP states
//   const [emailOtp, setEmailOtp] = useState(["", "", "", ""]);
//   const [phoneOtp, setPhoneOtp] = useState(["", "", "", ""]);

//   const [showEmailOtp, setShowEmailOtp] = useState(false);
//   const [showPhoneOtp, setShowPhoneOtp] = useState(false);
//   const [emailVerified, setEmailVerified] = useState(false);
//   const [phoneVerified, setPhoneVerified] = useState(false);

//   const emailOtpRefs = useRef([]);
//   const phoneOtpRefs = useRef([]);

//   // Timer states (independent for email & phone)
//   const [emailTimer, setEmailTimer] = useState(0);
//   const [isEmailRunning, setIsEmailRunning] = useState(false);
//   const [phoneTimer, setPhoneTimer] = useState(0);
//   const [isPhoneRunning, setIsPhoneRunning] = useState(false);

//   // OTP input logic
//   const handleOtpChange = (otpArray, setOtp, refs, index, value) => {
//     if (/^[0-9]?$/.test(value)) {
//       const newOtp = [...otpArray];
//       newOtp[index] = value;
//       setOtp(newOtp);
//       if (value && index < refs.current.length - 1) {
//         refs.current[index + 1].focus();
//       }
//     }
//   };

//   const handleKeyDown = (otpArray, refs, index, e) => {
//     if (e.key === "Backspace" && !otpArray[index] && index > 0) {
//       refs.current[index - 1].focus();
//     }
//   };

//   // Separate timer effects
//   useEffect(() => {
//     let emailInterval;
//     if (isEmailRunning && emailTimer > 0) {
//       emailInterval = setInterval(() => {
//         setEmailTimer((prev) => prev - 1);
//       }, 1000);
//     } else if (emailTimer === 0) {
//       setIsEmailRunning(false);
//     }
//     return () => clearInterval(emailInterval);
//   }, [isEmailRunning, emailTimer]);

//   useEffect(() => {
//     let phoneInterval;
//     if (isPhoneRunning && phoneTimer > 0) {
//       phoneInterval = setInterval(() => {
//         setPhoneTimer((prev) => prev - 1);
//       }, 1000);
//     } else if (phoneTimer === 0) {
//       setIsPhoneRunning(false);
//     }
//     return () => clearInterval(phoneInterval);
//   }, [isPhoneRunning, phoneTimer]);

//   // Handlers for resend clicks
//   const handleResendEmail = () => {
//     if (isEmailRunning) return;
//     setEmailTimer(30);
//     setIsEmailRunning(true);
//     console.log("Email OTP resent!");
//   };

//   const handleResendPhone = () => {
//     if (isPhoneRunning) return;
//     setPhoneTimer(30);
//     setIsPhoneRunning(true);
//     console.log("Phone OTP resent!");
//   };

//   // Format time (e.g., 30 → "00:30")
//   const formatTime = (s) => {
//     const m = Math.floor(s / 60)
//       .toString()
//       .padStart(2, "0");
//     const sec = (s % 60).toString().padStart(2, "0");
//     return `${m}:${sec}`;
//   };

//   // Dropdown options
//   const stateOptions = [
//     { value: "maharashtra", label: "Maharashtra" },
//     { value: "gujarat", label: "Gujarat" },
//     { value: "karnataka", label: "Karnataka" },
//     { value: "delhi", label: "Delhi" },
//   ];

//   const cityOptions = [
//     { value: "pune", label: "Pune" },
//     { value: "mumbai", label: "Mumbai" },
//     { value: "bangalore", label: "Bangalore" },
//     { value: "delhi", label: "Delhi" },
//   ];

//   const submitEmailOtp = () => {
//     setShowEmailOtp(false);
//     setEmailVerified(true);
//   };

//   const submitPhoneOtp = () => {
//     setShowPhoneOtp(false);
//     setPhoneVerified(true);
//   };

//   return (
//     <div className="stepform_form">
//       <form>

//         <div className="form-group">
//           <div className="row">
//             {/* Email */}
//             <div className="col-12 mt-3">
//               <div className="form-group__element verify_fields_holder">
//                 <Form.Control
//                   type="email"
//                   placeholder="Email Address"
//                   disabled={emailVerified}
//                 />
//                 {!emailVerified && !showEmailOtp && (
//                   <span
//                     className="verify_text"
//                     onClick={() => setShowEmailOtp(true)}
//                   >
//                     Verify
//                   </span>
//                 )}
//                 {emailVerified && (
//                   <span className="verify_text">
//                     <FaCheckCircle /> Verified
//                   </span>
//                 )}
//               </div>

//               {/* Email OTP */}
//               {showEmailOtp && !emailVerified && (
//                 <div className="otp-container mt-2">
//                   <span
//                     className={`resend_otp ${isEmailRunning ? "disabled" : ""}`}
//                     onClick={handleResendEmail}
//                     style={{
//                       cursor: isEmailRunning ? "not-allowed" : "pointer",
//                       color: isEmailRunning ? "#888" : "#007bff",
//                     }}
//                   >
//                     RESEND OTP{" "}
//                     <sub>
//                       {isEmailRunning ? `${formatTime(emailTimer)} Sec` : ""}
//                     </sub>
//                   </span>
//                   <div className="otp-boxes">
//                     {emailOtp.map((digit, index) => (
//                       <input
//                         key={index}
//                         type="text"
//                         maxLength="1"
//                         value={digit}
//                         ref={(el) => (emailOtpRefs.current[index] = el)}
//                         onChange={(e) =>
//                           handleOtpChange(
//                             emailOtp,
//                             setEmailOtp,
//                             emailOtpRefs,
//                             index,
//                             e.target.value
//                           )
//                         }
//                         onKeyDown={(e) =>
//                           handleKeyDown(emailOtp, emailOtpRefs, index, e)
//                         }
//                         className="otp-input"
//                       />
//                     ))}
//                   </div>
//                   <span className="verify_text" onClick={submitEmailOtp}>
//                     Submit
//                   </span>
//                 </div>
//               )}
//             </div>

//             {/* Phone */}
//             <div className="col-12 mt-3">
//               <div className="form-group__element verify_fields_holder">
//                 <Form.Control
//                   type="text"
//                   placeholder="Phone Number"
//                   disabled={phoneVerified}
//                 />
//                 {!phoneVerified && !showPhoneOtp && (
//                   <span
//                     className="verify_text"
//                     onClick={() => setShowPhoneOtp(true)}
//                   >
//                     Verify
//                   </span>
//                 )}
//                 {phoneVerified && (
//                   <span className="verify_text">
//                     <FaCheckCircle /> Verified
//                   </span>
//                 )}
//               </div>

//               {/* Phone OTP */}
//               {showPhoneOtp && !phoneVerified && (
//                 <div className="otp-container mt-2">
//                   <span
//                     className={`resend_otp ${isPhoneRunning ? "disabled" : ""}`}
//                     onClick={handleResendPhone}
//                     style={{
//                       cursor: isPhoneRunning ? "not-allowed" : "pointer",
//                       color: isPhoneRunning ? "#888" : "#007bff",
//                     }}
//                   >
//                     RESEND OTP{" "}
//                     <sub>
//                       {isPhoneRunning ? `${formatTime(phoneTimer)} Sec` : ""}
//                     </sub>
//                   </span>
//                   <div className="otp-boxes">
//                     {phoneOtp.map((digit, index) => (
//                       <input
//                         key={index}
//                         type="text"
//                         maxLength="1"
//                         value={digit}
//                         ref={(el) => (phoneOtpRefs.current[index] = el)}
//                         onChange={(e) =>
//                           handleOtpChange(
//                             phoneOtp,
//                             setPhoneOtp,
//                             phoneOtpRefs,
//                             index,
//                             e.target.value
//                           )
//                         }
//                         onKeyDown={(e) =>
//                           handleKeyDown(phoneOtp, phoneOtpRefs, index, e)
//                         }
//                         className="otp-input"
//                       />
//                     ))}
//                   </div>
//                   <span className="verify_text" onClick={submitPhoneOtp}>
//                     Submit
//                   </span>
//                 </div>
//               )}
//             </div>

//             {/* Alternate Phone */}
//             <div className="col-12 mt-3">
//               <div className="form-group__element">
//                 <Form.Control
//                   type="text"
//                   placeholder="Alternate Phone Number"
//                 />
//               </div>
//             </div>

//             {/* Address */}
//             <div className="col-12 mt-3">
//               <div className="form-group__element">
//                 <Form.Control
//                   as="textarea"
//                   rows={1}
//                   placeholder="Full Address"
//                 />
//               </div>
//             </div>

//             {/* City */}
//             <div className="col-md-6 mt-3">
//               <div className="form-group__element">
//                 <Select options={cityOptions} placeholder="Select City" />
//               </div>
//             </div>

//             {/* Pincode */}
//             <div className="col-md-6 mt-3">
//               <div className="form-group__element">
//                 <Form.Control type="text" placeholder="Pincode" />
//               </div>
//             </div>

//             {/* State */}
//             <div className="col-md-6 mt-3">
//               <div className="form-group__element">
//                 <Select options={stateOptions} placeholder="Select State" />
//               </div>
//             </div>

//     <div className="col-12 mt-3">
//       <Form.Group className="mb-3" controlId="formBasicCheckbox">
//         <Form.Check
//           type="checkbox"
//           label={
//             <>
//               By continuing, I agree to the{" "}
//               <Link to={"/terms-ans-conditions"}>
//                 <b>Terms & Conditions</b>
//               </Link>{" "}
//               &{" "}
//               <Link to={"/privacy-policy"}>
//                 <b>Privacy Policy</b>
//               </Link>
//             </>
//           }
//         />
//       </Form.Group>
//     </div>
//   </div>
// </div>

//         {/* Buttons */}
//         <div className="buttons" style={{ textAlign: "center" }}>

//           <Button className="search_btn form_btn mt-4" onClick={nextStep}>
//             Save and Next
//           </Button>
//         </div>
//       </form>
//     </div>
//   );
// }

// export default Personal_Details;
